import React, { memo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Chart colors
const CHART_COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  orange: '#F97316'
};

const BarChartComponent = memo(({ 
  data, 
  title, 
  xKey, 
  yKey, 
  color = CHART_COLORS.primary, 
  loading = false,
  layout = 'vertical', // 'vertical' or 'horizontal'
  showGrid = true,
  tooltipFormatter = null,
  customTooltip = null,
  barName = null,
  className = '',
  height = 300
}) => {
  const { t } = useTranslation();

  // Default tooltip formatter
  const defaultTooltipFormatter = tooltipFormatter || ((value, name) => [value, name]);

  return (
    <div className={`card ${className}`}>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      {loading || !data || data.length === 0 ? (
        <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
          {loading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          ) : (
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.noDataAvailable')}</p>
            </div>
          )}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart 
            data={data} 
            layout={layout === 'horizontal' ? 'horizontal' : 'vertical'}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {layout === 'horizontal' ? (
              <>
                <XAxis type="number" />
                <YAxis dataKey={xKey} type="category" width={layout === 'horizontal' ? 80 : 60} />
              </>
            ) : (
              <>
                <XAxis dataKey={xKey} />
                <YAxis />
              </>
            )}
            {customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip formatter={defaultTooltipFormatter} />
            )}
            <Bar 
              dataKey={yKey} 
              fill={color} 
              name={barName || yKey}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
});

BarChartComponent.displayName = 'BarChartComponent';

export default BarChartComponent;
